/** Shared chart animation hook — eliminates per-chart copy-paste */

import { interpolate } from "./remotion-shim";
import type { ChartData, ChartRenderContext } from "./types";

export interface ChartAnimationConfig {
  delay?: number;
  enterDuration?: number;
  progressStart?: number;
  progressEnd?: number;
}

interface StaggerResult {
  opacity: number;
  scale: number;
  progress: number;
  eased: number;
}

export interface ChartAnimationResult {
  /** Seconds since chart delay elapsed (0 = chart just appeared) */
  delayedElapsed: number;
  /** Frames since chart delay elapsed */
  enterFrame: number;
  /** Container enter opacity (0→1 over 15 frames) */
  chartOpacity: number;
  /** Container enter scale (0.9→1 over 15 frames) */
  chartScale: number;
  /** Overall draw progress 0→1 */
  progress: number;
  /** Per-element stagger — returns animated values for item at index */
  stagger: (index: number, staggerDelay?: number) => StaggerResult;
  /** Cubic ease-out */
  easeOut: (t: number) => number;
}

export function useChartAnimation(
  ctx: ChartRenderContext,
  chartData: ChartData,
  config?: ChartAnimationConfig,
): ChartAnimationResult {
  const t = ctx.frame / ctx.fps;
  const elapsed = t - ctx.slideStart;
  const chartDelay = chartData.delay ?? config?.delay ?? 1.5;
  const delayedElapsed = Math.max(0, elapsed - chartDelay);
  const enterDuration = config?.enterDuration ?? 15;
  const enterFrame = delayedElapsed * ctx.fps;

  const chartOpacity = interpolate(enterFrame, [0, enterDuration], [0, 1], {
    extrapolateRight: "clamp",
  });
  const chartScale = interpolate(enterFrame, [0, enterDuration], [0.9, 1], {
    extrapolateRight: "clamp",
  });

  const progressStart = config?.progressStart ?? 0.5;
  const progressEnd = config?.progressEnd ?? 2.5;
  const progress = interpolate(
    delayedElapsed,
    [progressStart, progressEnd],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );

  const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

  const stagger = (index: number, staggerDelay = 0.2): StaggerResult => {
    const offset = index * staggerDelay;
    const itemProgress = interpolate(
      delayedElapsed,
      [0.4 + offset, 1.0 + offset],
      [0, 1],
      { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
    );
    const eased = easeOut(itemProgress);
    const opacity = interpolate(
      delayedElapsed,
      [0.3 + offset, 0.5 + offset],
      [0, 1],
      { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
    );
    const scale = interpolate(
      delayedElapsed,
      [0.4 + offset, 0.9 + offset],
      [0.85, 1],
      { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
    );
    return { opacity, scale, progress: eased, eased };
  };

  return {
    delayedElapsed,
    enterFrame,
    chartOpacity,
    chartScale,
    progress,
    stagger,
    easeOut,
  };
}
